import React, { useState, useEffect } from 'react';
import { loadUsers } from '../utils/storage';

const NewClientForm = ({ onSave, onCancel }) => {
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [showCustomSpecialtyInput, setShowCustomSpecialtyInput] = useState(false);
  const [customSpecialty, setCustomSpecialty] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const allUsers = await loadUsers();
        // Filtrar solo usuarios estándar
        const standardUsers = allUsers.filter(user => user.role === 'standard');
        setUsers(standardUsers);
      } catch (error) {
        console.error('Error al cargar usuarios:', error);
        setUsers([]);
      }
    };
    fetchUsers();
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    cells: [], // Cambiado a array para múltiples células
    specialty: '',
    email: '',
    country: 'México',
    googleAdsLink: '',
    googleMapsLink: '',
    facebookLink: '',
    metaAccountLink: '',
    salesExecutives: [] // Cambiado a array para múltiples ejecutivos
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'specialty' && value === 'custom') {
      setShowCustomSpecialtyInput(true);
      setFormData(prev => ({ ...prev, specialty: '' }));
    } else {
      setShowCustomSpecialtyInput(false);
      setCustomSpecialty('');
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCustomSpecialtyChange = (e) => {
    setCustomSpecialty(e.target.value);
    setFormData(prev => ({ ...prev, specialty: e.target.value }));
  };

  const validateEmail = (email) => {
    if (!email) return true;
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Nombre es requerido';
    if (formData.salesExecutives.length === 0) newErrors.salesExecutives = 'Debe seleccionar al menos un ejecutivo de ventas';
    
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Ingrese un email válido';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    try {
      // Preparar datos para guardar - mantener compatibilidad con estructura existente
      const clientData = {
        ...formData,
        // Para compatibilidad, usar el primer ejecutivo como salesExecutive principal
        salesExecutive: formData.salesExecutives[0] || '',
        // Mantener el array para futuras funcionalidades
        salesExecutives: formData.salesExecutives,
        // Para compatibilidad, usar la primera célula como cell principal
        cell: formData.cells[0] || '',
        // Mantener el array para futuras funcionalidades
        cells: formData.cells
      };
      
      await onSave(clientData);
      alert('Cliente creado exitosamente');
      onCancel();
    } catch (error) {
      alert('Error al crear el cliente');
      console.error(error);
    }
  };

  const specialties = [
    'Medicina General', 'Cardiología', 'Dermatología', 'Endocrinología', 'Gastroenterología',
    'Ginecología y Obstetricia', 'Neurología', 'Oncología', 'Oftalmología', 'Ortopedia y Traumatología',
    'Otorrinolaringología', 'Pediatría', 'Psiquiatría', 'Psicología', 'Reumatología',
    'Urología', 'Neumología', 'Cirugía General', 'Cirugía Laparoscópica', 'Cirugía Plástica y Reconstructiva',
    'Cirugía Cardiovascular', 'Cirugía Pediátrica', 'Cirugía Oncológica', 'Cirugía Coloproctológica',
    'Cirugía Maxilofacial', 'Medicina Interna', 'Nefrología', 'Hematología', 'Medicina Física y Rehabilitación',
    'Anestesiología', 'Medicina Familiar', 'Infectología', 'Radiología', 'Endodoncia (odontología especializada)',
    'Odontología (Dentista)', 'Nutriología', 'Terapia Física', 'Medicina del Deporte', 'Toxicología',
    'Genética Médica', 'Fisioterapia', 'Terapia Ocupacional', 'Logopedia (Terapia del lenguaje)'
  ];

  const countries = [
    'México', 'Estados Unidos', 'Canadá', 'España', 'Colombia', 'Perú', 'Ecuador', 'Panamá',
    'República Dominicana', 'El Salvador', 'Chile', 'Honduras', 'Guatemala', 'Belice', 'Paraguay',
    'Uruguay', 'Bolivia'
  ];

  const cells = [
    'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4'
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold text-[#0061A8] mb-4">Nuevo Cliente</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Nombre completo <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`mt-1 block w-full border ${errors.name ? 'border-red-500' : 'border-[#D1D3D4]'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]`}
            required
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Células (seleccione múltiples)
          </label>
          <div className="mt-1 grid grid-cols-3 gap-2 max-h-40 overflow-y-auto border border-[#D1D3D4] rounded-md p-3">
            {cells.filter(cell => cell !== '').map(cell => (
              <label key={cell} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.cells.includes(cell)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData(prev => ({
                        ...prev,
                        cells: [...prev.cells, cell]
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        cells: prev.cells.filter(c => c !== cell)
                      }));
                    }
                  }}
                  className="rounded border-[#D1D3D4] text-[#0061A8] focus:ring-[#0061A8]"
                />
                <span className="text-sm text-[#63666A]">{cell}</span>
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-[#63666A]">
            Células seleccionadas: {formData.cells.length > 0 ? formData.cells.join(', ') : 'Ninguna'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Especialidad
          </label>
          <select
            name="specialty"
            value={showCustomSpecialtyInput ? 'custom' : formData.specialty}
            onChange={handleChange}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          >
            <option value="">Seleccione una especialidad</option>
            {specialties.map(spec => (
              <option key={spec} value={spec}>{spec}</option>
            ))}
            <option value="custom">Otra (especificar)</option>
          </select>
          {showCustomSpecialtyInput && (
            <input
              type="text"
              value={customSpecialty}
              onChange={handleCustomSpecialtyChange}
              placeholder="Especifique especialidad"
              className="mt-2 w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Ej. contacto@clinica.com"
            className={`mt-1 block w-full border ${errors.email ? 'border-red-500' : 'border-[#D1D3D4]'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]`}
          />
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            País
          </label>
          <select
            name="country"
            value={formData.country}
            onChange={handleChange}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          >
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Link Google Ads
          </label>
          <input
            type="url"
            name="googleAdsLink"
            value={formData.googleAdsLink}
            onChange={handleChange}
            placeholder="https://ads.google.com/..."
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Link Google Maps
          </label>
          <input
            type="url"
            name="googleMapsLink"
            value={formData.googleMapsLink}
            onChange={handleChange}
            placeholder="https://maps.app.goo.gl/..."
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Link Facebook
          </label>
          <input
            type="url"
            name="facebookLink"
            value={formData.facebookLink}
            onChange={handleChange}
            placeholder="https://facebook.com/..."
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Link Cuenta de Meta
          </label>
          <input
            type="url"
            name="metaAccountLink"
            value={formData.metaAccountLink}
            onChange={handleChange}
            placeholder="https://business.facebook.com/..."
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">
            Ejecutivos de Ventas (seleccione múltiples) <span className="text-red-500">*</span>
          </label>
          <div className="mt-1 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-[#D1D3D4] rounded-md p-3">
            {users.map(user => (
              <label key={user.email} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.salesExecutives.includes(user.email)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData(prev => ({
                        ...prev,
                        salesExecutives: [...prev.salesExecutives, user.email]
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        salesExecutives: prev.salesExecutives.filter(email => email !== user.email)
                      }));
                    }
                    // Limpiar error si se selecciona al menos uno
                    if (errors.salesExecutives) {
                      setErrors(prev => ({ ...prev, salesExecutives: '' }));
                    }
                  }}
                  className="rounded border-[#D1D3D4] text-[#0061A8] focus:ring-[#0061A8]"
                />
                <span className="text-sm text-[#63666A]">{user.firstName} {user.lastName}</span>
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-[#63666A]">
            Ejecutivos seleccionados: {formData.salesExecutives.length > 0 ? 
              formData.salesExecutives.map(email => {
                const user = users.find(u => u.email === email);
                return user ? `${user.firstName} ${user.lastName}` : email;
              }).join(', ') : 'Ninguno'}
          </p>
          {errors.salesExecutives && <p className="mt-1 text-sm text-red-600">{errors.salesExecutives}</p>}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="border border-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="bg-[#0061A8] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
          >
            Guardar Cliente
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewClientForm;
